/**
 * Shipping Countries Configuration
 *
 * List of countries where Higlo Hearths can be shipped.
 * Currently supports: United States, Canada, United Kingdom, and EU member states.
 *
 * Keep in lockstep with the other copies (no shared library, by design):
 *   - cloud/cloudfunction/functions/shippingAddressRules.js (/user/confirmShippingAddresses)
 *   - tools/ops/handover/lib/row-to-account.js (handover importer)
 *   - marketing website-gen2 site/src/lib/shippingCountries.js and
 *     backend/functions/lib/shippingCountries.js (the order form)
 */

export const ALLOWED_SHIPPING_COUNTRIES = [
  // North America
  { code: 'US', name: 'United States', postcodeLabel: 'ZIP Code', postcodePattern: /^\d{5}(-\d{4})?$/ },
  { code: 'CA', name: 'Canada', postcodeLabel: 'Postal code', postcodePattern: /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/ },

  // United Kingdom
  { code: 'GB', name: 'United Kingdom', postcodeLabel: 'Postcode', postcodePattern: /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i },

  // EU Countries (27 member states, alphabetically)
  { code: 'AT', name: 'Austria', postcodeLabel: 'Postcode', postcodePattern: /^\d{4}$/ },
  { code: 'BE', name: 'Belgium', postcodeLabel: 'Postcode', postcodePattern: /^\d{4}$/ },
  { code: 'BG', name: 'Bulgaria', postcodeLabel: 'Postcode', postcodePattern: /^\d{4}$/ },
  { code: 'HR', name: 'Croatia', postcodeLabel: 'Postcode', postcodePattern: /^\d{5}$/ },
  { code: 'CY', name: 'Cyprus', postcodeLabel: 'Postcode', postcodePattern: /^\d{4}$/ },
  { code: 'CZ', name: 'Czech Republic', postcodeLabel: 'Postcode', postcodePattern: /^\d{3}\s?\d{2}$/ },
  { code: 'DK', name: 'Denmark', postcodeLabel: 'Postcode', postcodePattern: /^\d{4}$/ },
  { code: 'EE', name: 'Estonia', postcodeLabel: 'Postcode', postcodePattern: /^\d{5}$/ },
  { code: 'FI', name: 'Finland', postcodeLabel: 'Postcode', postcodePattern: /^\d{5}$/ },
  { code: 'FR', name: 'France', postcodeLabel: 'Postcode', postcodePattern: /^\d{5}$/ },
  { code: 'DE', name: 'Germany', postcodeLabel: 'Postcode', postcodePattern: /^\d{5}$/ },
  { code: 'GR', name: 'Greece', postcodeLabel: 'Postcode', postcodePattern: /^\d{3}\s?\d{2}$/ },
  { code: 'HU', name: 'Hungary', postcodeLabel: 'Postcode', postcodePattern: /^\d{4}$/ },
  { code: 'IE', name: 'Ireland', postcodeLabel: 'Eircode', postcodePattern: /^[A-Z]\d{2}\s?[A-Z0-9]{4}$/i },
  { code: 'IT', name: 'Italy', postcodeLabel: 'Postcode', postcodePattern: /^\d{5}$/ },
  { code: 'LV', name: 'Latvia', postcodeLabel: 'Postcode', postcodePattern: /^LV-\d{4}$/i },
  { code: 'LT', name: 'Lithuania', postcodeLabel: 'Postcode', postcodePattern: /^LT-\d{5}$/i },
  { code: 'LU', name: 'Luxembourg', postcodeLabel: 'Postcode', postcodePattern: /^\d{4}$/ },
  { code: 'MT', name: 'Malta', postcodeLabel: 'Postcode', postcodePattern: /^[A-Z]{3}\s?\d{4}$/i },
  { code: 'NL', name: 'Netherlands', postcodeLabel: 'Postcode', postcodePattern: /^\d{4}\s?[A-Z]{2}$/i },
  { code: 'PL', name: 'Poland', postcodeLabel: 'Postcode', postcodePattern: /^\d{2}-\d{3}$/ },
  { code: 'PT', name: 'Portugal', postcodeLabel: 'Postcode', postcodePattern: /^\d{4}-\d{3}$/ },
  { code: 'RO', name: 'Romania', postcodeLabel: 'Postcode', postcodePattern: /^\d{6}$/ },
  { code: 'SK', name: 'Slovakia', postcodeLabel: 'Postcode', postcodePattern: /^\d{3}\s?\d{2}$/ },
  { code: 'SI', name: 'Slovenia', postcodeLabel: 'Postcode', postcodePattern: /^\d{4}$/ },
  { code: 'ES', name: 'Spain', postcodeLabel: 'Postcode', postcodePattern: /^\d{5}$/ },
  { code: 'SE', name: 'Sweden', postcodeLabel: 'Postcode', postcodePattern: /^\d{3}\s?\d{2}$/ },
];

/**
 * Get country object by 2-letter code
 * @param {string} code - 2-letter country code (e.g., 'US', 'GB')
 * @returns {Object|undefined} Country object or undefined if not found
 */
export const getCountryByCode = (code) => {
  if (!code) return undefined;
  return ALLOWED_SHIPPING_COUNTRIES.find(c => c.code.toUpperCase() === code.toUpperCase());
};

/**
 * Get array of country codes for Google Maps country restrictions
 * @returns {string[]} Array of lowercase country codes
 */
export const getGoogleMapsCountryCodes = () => {
  return ALLOWED_SHIPPING_COUNTRIES.map(c => c.code.toLowerCase());
};

/**
 * Validate postcode/ZIP format for a specific country
 * @param {string} postcode - The postcode to validate
 * @param {string} countryCode - 2-letter country code
 * @returns {boolean} True if valid format
 */
export const validatePostcode = (postcode, countryCode) => {
  const country = getCountryByCode(countryCode);
  if (!country || !country.postcodePattern) return true; // If no pattern, skip validation
  return country.postcodePattern.test(postcode);
};

/**
 * Get postcode label for a specific country (e.g., "ZIP Code" vs "Postcode")
 * @param {string} countryCode - 2-letter country code
 * @returns {string} Label for postcode field
 */
export const getPostcodeLabel = (countryCode) => {
  const country = getCountryByCode(countryCode);
  return country ? country.postcodeLabel : 'Postcode';
};

/**
 * Check if a country code is in the allowed shipping list
 * @param {string} code - 2-letter country code
 * @returns {boolean} True if shipping is allowed to this country
 */
export const isShippingAllowed = (code) => {
  return getCountryByCode(code) !== undefined;
};

/**
 * State / province / county. Carriers need it for the US and Canada, so there
 * it's a required pick from a fixed list, stored as the 2-letter postal code.
 * Elsewhere it's optional free text: "County" in the UK and Ireland, "Region"
 * for the EU. Same table and rules as the marketing order form.
 */
export const US_STATES = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
  ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
  ['DC', 'District of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'],
  ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'],
  ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'],
  ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
  ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'],
  ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'],
  ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'],
  ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'],
  ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'],
  ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
].map(([code, name]) => ({ code, name }));

export const CA_PROVINCES = [
  ['AB', 'Alberta'], ['BC', 'British Columbia'], ['MB', 'Manitoba'], ['NB', 'New Brunswick'],
  ['NL', 'Newfoundland and Labrador'], ['NT', 'Northwest Territories'], ['NS', 'Nova Scotia'],
  ['NU', 'Nunavut'], ['ON', 'Ontario'], ['PE', 'Prince Edward Island'], ['QC', 'Quebec'],
  ['SK', 'Saskatchewan'], ['YT', 'Yukon'],
].map(([code, name]) => ({ code, name }));

const STATE_CONFIG = {
  US: { label: 'State', required: true, options: US_STATES },
  CA: { label: 'Province or territory', required: true, options: CA_PROVINCES },
  GB: { label: 'County', required: false, options: null },
  IE: { label: 'County', required: false, options: null },
};
const DEFAULT_STATE_CONFIG = { label: 'Region', required: false, options: null };

/**
 * State/province field config for a country: { label, required, options }.
 * `options` is the fixed list for US/CA, null for free text.
 */
export const getStateConfig = (countryCode) =>
  STATE_CONFIG[(countryCode || '').toUpperCase()] || DEFAULT_STATE_CONFIG;

/**
 * True when the state is acceptable for the country: a known 2-letter code
 * (case-insensitive) for US/CA; anything, including empty, elsewhere.
 */
export const validateState = (state, countryCode) => {
  const cfg = getStateConfig(countryCode);
  if (!cfg.required) return true;
  const code = (state || '').trim().toUpperCase();
  return cfg.options.some((o) => o.code === code);
};

/**
 * The state as it should be displayed: the upper-case code for US/CA, the
 * trimmed free text elsewhere, '' when absent.
 */
export const displayState = (state, countryCode) => {
  const s = (state || '').trim();
  return getStateConfig(countryCode).options ? s.toUpperCase() : s;
};

/**
 * Full country name for a 2-letter code, falling back to the code itself.
 */
export const getCountryName = (code) => getCountryByCode(code)?.name || code || '';
